// components/industry-templates/RenewableEnergyEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sun, Wind, Zap, Leaf } from 'lucide-react';

export const RenewableEnergyEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Energy Solutions */}
      {industryData?.energy_solutions && (
        <Card className="border-0 bg-gradient-to-br from-green-500/20 to-lime-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Sun className="w-8 h-8" style={{ color: theme.primary }} />
              Renewable Energy Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.energy_solutions.map((solution: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{solution.type === 'solar' ? '☀️' : solution.type === 'wind' ? '💨' : '⚡'}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{solution.name}</h3>
                      <p className="text-slate-300 text-sm mb-3">{solution.description}</p>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>{solution.capacity}</Badge>
                        <Badge variant="outline" className="border-white/20 text-white">{solution.efficiency}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sustainability Goals */}
      {industryData?.sustainability_goals && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Leaf className="w-6 h-6" style={{ color: theme.primary }} />
              Sustainability Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-5 bg-white/5 rounded-lg">
                <p className="text-4xl font-bold" style={{ color: theme.primary }}>
                  {industryData.sustainability_goals.carbon_reduction}
                </p>
                <p className="text-sm text-slate-400 mt-2">CO₂ Reduction</p>
              </div>
              <div className="text-center p-5 bg-white/5 rounded-lg">
                <p className="text-4xl font-bold" style={{ color: theme.primary }}>
                  {industryData.sustainability_goals.renewable_capacity}
                </p>
                <p className="text-sm text-slate-400 mt-2">Clean Energy</p>
              </div>
              <div className="text-center p-5 bg-white/5 rounded-lg">
                <p className="text-4xl font-bold" style={{ color: theme.primary }}>
                  {industryData.sustainability_goals.jobs_created}
                </p>
                <p className="text-sm text-slate-400 mt-2">Green Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Technology Showcase */}
      {industryData?.technology_showcase && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wind className="w-6 h-6" style={{ color: theme.primary }} />
              Clean Technology Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.technology_showcase.map((tech: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{tech.innovation}</h4>
                  <p className="text-sm text-slate-400 mb-2">{tech.company}</p>
                  <Badge style={{ backgroundColor: theme.primary }}>{tech.stage}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Policy Sessions */}
      {industryData?.policy_sessions && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-6 h-6" style={{ color: theme.primary }} />
              Policy & Regulations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-3">Expert panels on renewable energy policy:</p>
              <div className="flex flex-wrap gap-2">
                {industryData.policy_sessions.topics?.map((topic: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white">
                    {topic}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
